import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

function BookingManagePage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cancelled, setCancelled] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-5xl mb-4">!</div>
            <h2 className="text-xl font-semibold text-red-900 mb-2">Invalid Link</h2>
            <p className="text-red-700">
              This link is missing required verification. Please use the link from your confirmation email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await api.cancelBooking(bookingId, token);
      setCancelled(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel booking');
    } finally {
      setProcessing(false);
    }
  };

  const handleReschedule = async () => {
    try {
      setProcessing(true);
      setError(null);
      const result = await api.rescheduleBooking(bookingId, token);

      if (result.newLinkId) {
        navigate(`/book/${result.newLinkId}`, {
          state: {
            message: 'Your previous appointment has been cancelled. Please select a new time below.'
          }
        });
      } else {
        navigate('/', {
          state: {
            message: 'Your previous booking has been cancelled. Please schedule a new appointment.'
          }
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reschedule booking');
      setProcessing(false);
    }
  };

  if (cancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
              <svg
                className="h-10 w-10 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Booking Cancelled
            </h2>

            <p className="text-gray-600 mb-6">
              Your booking has been cancelled. You will receive a confirmation email shortly.
            </p>

            <button
              onClick={() => navigate('/')}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            Manage Your Booking
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">Booking ID</p>
            <p className="font-mono text-sm font-semibold text-gray-900">{bookingId}</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleReschedule}
              disabled={processing}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {processing ? 'Processing...' : 'Reschedule Booking'}
            </button>

            <button
              onClick={handleCancel}
              disabled={processing}
              className="w-full bg-white text-red-600 py-3 px-4 rounded-lg font-medium border border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              {processing ? 'Processing...' : 'Cancel Booking'}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6">
            Need help? Contact support@impetra.com
          </p>
        </div>
      </div>
    </div>
  );
}

export default BookingManagePage;
